import { Link } from 'react-router';

import { useTranslation } from 'react-i18next';

import { Card, Tag, Typography } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import { colorTag } from 'utils/formatDocumentTag';

import SearchResultCardImage from './SearchResultCardImage';

import { ThumbsDown, ThumbsUp } from '@/icons';

export interface SearchResultCardProps {
  documentId: string;
  documentType: string;
  featureFlagSearchResultFeedback: boolean;
  getFeedbackText: (id?: string) => React.ReactNode;
  icon: string;
  id: string;
  isMarketTest: boolean;
  isThumbsDown: boolean;
  isThumbsUp: boolean;
  isTridion: boolean;
  lastModified: string;
  locationTypes: string[];
  name: string;
  url: {
    base: string;
    translated: string;
  };
  onClick: (props: {
    icon: string;
    resultId: string;
    documentId: string;
    isTridion: boolean;
    lastModified: string;
    url: {
      base: string;
      translated: string;
    };
    name: string;
    tags: string[];
  }) => void;
  onThumbsDown: (id: string, resultId: string) => void;
  onThumbsUp: (id: string, resultId: string) => void;
  resultId: string;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  id,
  documentId,
  documentType,
  featureFlagSearchResultFeedback,
  getFeedbackText,
  icon,
  isMarketTest,
  isThumbsDown,
  isThumbsUp,
  isTridion,
  lastModified,
  locationTypes,
  name,
  onClick,
  onThumbsDown,
  onThumbsUp,
  resultId,
  url,
}) => {
  const { t } = useTranslation();

  return (
    <Link
      id="category"
      onClick={e => {
        e.preventDefault();
        onClick({
          icon,
          resultId,
          documentId,
          isTridion,
          lastModified,
          url,
          name,
          tags: locationTypes,
        });
      }}
      to={`/doc/${documentId}`}
    >
      <Card
        elevation={1}
        id={`a${id}`}
        key={id}
        title={name}
        variant="default"
        className="animate-fade-in! border-grayscale-gray2! my-4! flex! cursor-pointer! flex-row! items-center! border! p-4!"
      >
        <SearchResultCardImage
          documentType={documentType}
          icon={icon}
          name={name}
        />
        <div className="w-full">
          <div className="flex flex-row items-center">
            <Typography
              variant="body1"
              className="text-brand-navy! dark:text-contrast!"
            >
              {name}
            </Typography>
            {isMarketTest && (
              <Tag
                label={t('Generic.marketTest')}
                variant="filled"
                className="ml-2.5"
              />
            )}
            {!!locationTypes.length &&
              locationTypes.map((tag, index) => (
                <Tag
                  color={colorTag(tag)}
                  key={index}
                  label={tag}
                  variant="filled"
                  className="ml-2.5"
                />
              ))}
          </div>
          {featureFlagSearchResultFeedback && (
            <Typography
              variant="body2"
              className="text-grayscale-gray7! flex! items-center!"
            >
              {getFeedbackText(id)}
              <Typography
                variant="body2"
                className="max-w-full! grow! text-right!"
              >
                <ThumbsUp
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onThumbsUp(id, resultId);
                  }}
                  className={cn(
                    'hover:[&>path]:fill-brand-green size-[35px] cursor-pointer [&>path]:transition-colors',
                    {
                      '[&>path]:fill-brand-green': isThumbsUp,
                    },
                  )}
                />
                <span className="px-2.5" />
                <ThumbsDown
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onThumbsDown(id, resultId);
                  }}
                  className={cn(
                    'hover:[&>path]:fill-brand-red size-[35px] cursor-pointer [&>path]:transition-colors',
                    {
                      '[&>path]:fill-brand-red': isThumbsDown,
                    },
                  )}
                />
              </Typography>
            </Typography>
          )}
        </div>
      </Card>
    </Link>
  );
};

export default SearchResultCard;
